import { useCallback, useMemo } from 'react';
import { path } from 'ramda';

import { useTranslations } from '@core/js/hooks/i18n';
import {
    ITranslateMethod,
    TTranslationArguments
} from '@core/js/hooks/i18n/types';
import { fn } from '@core/js/utils/Validation/validationRules';

import { CUSTOM_VALIDATION_FIELDS, VALIDATION_FIELDS } from '@interlayer/js/app/hooks/useValidationRules/constants';
import { useCheckRegPinByDOB } from '@interlayer/js/app/hooks/useValidationRules/hooks/useCheckRegPinByDOB';

import { useCheckCities } from './hooks/useCheckCities';
import { useCheckEmailDuplicate } from './hooks/useCheckEmailDuplicate';
import { useCheckFoundTransferAmount } from './hooks/useCheckFoundTransferAmount';
import { useCheckMembership } from './hooks/useCheckMembership';
import { useCheckOccupation } from './hooks/useCheckOccupation';
import { useCheckPassword } from './hooks/useCheckPassword';
import { useCheckPin } from './hooks/useCheckPin';
import { useCheckRetailNameDuplicate } from './hooks/useCheckRetailNameDuplicate';
import { useCheckSearchAddress } from './hooks/useCheckSearchAddress';
import { useCheckZips } from './hooks/useCheckZips';
import { getValidationRules } from './utils/validationRules';
import {
    TAllValidationKeys,
    TGetValidationRules,
    TValidationConfig } from './types';

export const useValidationRules = <T extends TAllValidationKeys = []>(
    rules: T,
    tKeyPrefix?: string,
    additionalProps?: Record<string, unknown>
): TValidationConfig<T> => {
    const tLocal = useTranslations(`playtech.mobile.system.${tKeyPrefix}.validation.`);
    const tDefault = useTranslations('playtech.mobile.system.registration.validation.');
    const t: ITranslateMethod = useCallback((
        tKey: string | string[],
        ...args: TTranslationArguments
    ) => {
        const defaultValue = args[0];

        if (defaultValue) {
            if (Array.isArray(defaultValue)) {
                return tLocal(tKey, defaultValue, tDefault(tKey, defaultValue));
            }

            return tLocal(tKey, tLocal(defaultValue as string, tDefault(tKey, tDefault(defaultValue as string))));

        }

        return tLocal(tKey, tDefault(tKey));
    }, [tDefault, tLocal]);

    const checkZips = useCheckZips(t);
    const checkCities = useCheckCities(t);
    const checkPassword = useCheckPassword(t);
    const checkRegPinByDOB = useCheckRegPinByDOB(t);
    const checkSearchAddress = useCheckSearchAddress(t);
    const checkPin = useCheckPin(!rules.includes(VALIDATION_FIELDS.pin));
    const checkFoundTransferAmount = useCheckFoundTransferAmount(t);
    const checkMembership = useCheckMembership(t, !rules.includes(VALIDATION_FIELDS.membershipNumber));
    const checkRetailName = useCheckRetailNameDuplicate(t, !rules.includes(VALIDATION_FIELDS.userNameRetail));
    const checkOccupation = useCheckOccupation(t, additionalProps);
    const checkEmailDuplicate = useCheckEmailDuplicate(t, additionalProps, !rules.includes(CUSTOM_VALIDATION_FIELDS.duplicate));

    const validationRules = getValidationRules(t);

    const allValidationConfig: TGetValidationRules = useMemo(() => ({
        ...validationRules,
        ...checkOccupation,
        zip: [
            ...validationRules.zip,
            fn({ fn: checkZips })
        ],
        city: [
            ...validationRules.city,
            fn({ fn: checkCities })
        ],
        searchAddress: [
            fn({ fn: checkSearchAddress })
        ],
        password: [
            ...validationRules.password,
            fn({ fn: checkPassword })
        ],
        amount: checkFoundTransferAmount,
        membershipNumber: [
            ...validationRules.membershipNumber,
            fn({ fn: checkMembership })
        ],
        pin: [
            ...validationRules.pin,
            fn({ fn: checkPin })
        ],
        pinReg: [
            ...validationRules.pinReg,
            fn({ fn: checkRegPinByDOB })
        ],
        userNameRetail: [
            ...validationRules.userNameRetail,
            fn({ fn: checkRetailName })
        ],
        duplicate: {
            email: [
                ...validationRules.email,
                fn({ fn: checkEmailDuplicate })
            ]
        }
    }), [
        checkCities,
        checkEmailDuplicate,
        checkFoundTransferAmount,
        checkMembership,
        checkOccupation,
        checkPassword,
        checkPin,
        checkSearchAddress,
        checkZips,
        checkRegPinByDOB,
        validationRules,
        checkRetailName
    ]);

    return useMemo(() => {
        return rules
            .reduce((acc, rule) => {
                if (rule.includes('.')) {
                    const splitPath = rule.split('.');
                    acc[splitPath[1] as keyof TValidationConfig<T>] = path(splitPath, allValidationConfig) || [];
                } else {
                    acc[rule as keyof TValidationConfig<T>] =
                        path([rule as keyof TValidationConfig<T>], allValidationConfig) || [];
                }

                return acc;
            }, {} as TValidationConfig<T>);
    }, [allValidationConfig, rules]);
};
